import React, { useEffect, useState } from "react";
import { Film, Sparkles, Loader2, Trash2, Download, Share2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

const SIZE_OPTIONS = [
  { v: "1024x1792", l: "Vertical 9:16", desc: "TikTok / Reels / Stories" },
  { v: "1280x720", l: "Wide 16:9", desc: "YouTube / Web" },
  { v: "1024x1024", l: "Square 1:1", desc: "Instagram feed" },
];

const PROMPT_TEMPLATES = [
  "Cinematic slow-motion shot of a couple holding hands at golden hour, walking through a vineyard",
  "An overhead drone shot panning across a rooftop wedding reception with twinkling string lights at dusk",
  "Black and white close-up of a couple's first dance, soft jazz lighting, intimate emotional moment",
  "A bouquet toss scene in slow motion, bridesmaids reaching, flowers spinning in the air",
];

export default function ReelsPage() {
  const [reels, setReels] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(4);
  const [size, setSize] = useState("1024x1792");
  const [model, setModel] = useState("sora-2");
  const [busy, setBusy] = useState(false);

  async function load() {
    const { data } = await api.get("/reels/mine");
    setReels(data);
  }
  useEffect(() => {
    load().catch(() => {});
    // Poll every 15s for status updates
    const t = setInterval(() => load().catch(() => {}), 15000);
    return () => clearInterval(t);
  }, []);

  async function generate() {
    if (prompt.trim().length < 8) { toast.error("Add a richer prompt"); return; }
    setBusy(true);
    try {
      await api.post("/reels", { prompt: prompt.trim(), duration, size, model });
      toast.success("Reel queued — generation takes 2-5 minutes");
      setPrompt("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this reel?")) return;
    await api.delete(`/reels/${id}`);
    load();
  }

  return (
    <div data-testid="reels-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1 flex items-center gap-2">
            <Film className="w-3 h-3" /> AI Wedding Reels
          </p>
          <h1 className="font-serif text-4xl md:text-5xl">Sora-Powered <span className="font-script text-[var(--gold)]">Cinema</span></h1>
          <p className="text-sm text-[var(--muted)] mt-2 max-w-xl">
            Describe the moment you want to relive — we'll generate a cinematic clip you can share to TikTok, Reels, and the wedding site.
          </p>
        </div>
      </div>

      {/* Generator */}
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 mb-8" data-testid="reel-generator">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A cinematic slow-motion shot of a couple holding hands at golden hour…"
          rows={3}
          maxLength={600}
          data-testid="reel-prompt"
          className="input-luxe resize-none mb-3"
        />
        <div className="flex flex-wrap gap-2 mb-4">
          {PROMPT_TEMPLATES.map((t, i) => (
            <button
              key={i}
              onClick={() => setPrompt(t)}
              className="text-[11px] px-3 py-1.5 rounded-full bg-[var(--bg-warm)] hover:bg-[var(--gold)] hover:text-white transition-colors"
              data-testid={`template-${i}`}
            >
              <Sparkles className="w-3 h-3 inline mr-1" /> Template {i + 1}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Aspect</label>
            <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="reel-size" className="input-luxe">
              {SIZE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l} — {o.desc}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Duration</label>
            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="reel-duration" className="input-luxe">
              {[4, 8, 12].map((d) => <option key={d} value={d}>{d} seconds</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Quality</label>
            <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="reel-model" className="input-luxe">
              <option value="sora-2">Standard (faster)</option>
              <option value="sora-2-pro">Pro (higher detail)</option>
            </select>
          </div>
        </div>

        <button
          onClick={generate}
          disabled={busy}
          data-testid="reel-generate-btn"
          className="btn-gold w-full md:w-auto"
        >
          {busy ? "Queuing…" : "Generate cinematic reel"}
        </button>
        <p className="text-[11px] text-[var(--muted)] mt-3">Generation typically takes 2–5 minutes. The reel appears below when ready.</p>
      </div>

      {/* Library */}
      {reels.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--line)] rounded-2xl">
          <Film className="w-10 h-10 mx-auto text-[var(--gold)] mb-3" />
          <p className="font-serif text-2xl">No reels yet</p>
          <p className="text-sm text-[var(--muted)] mt-2">Generate your first cinematic clip above.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reels-library">
          {reels.map((r) => (
            <div key={r.reel_id} className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden" data-testid={`reel-${r.reel_id}`}>
              <div className="aspect-[9/16] bg-black relative overflow-hidden">
                {r.status === "ready" ? (
                  <video
                    src={`${BACKEND_URL}/api/files/${r.storage_path}`}
                    controls
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : r.status === "failed" ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
                    <AlertCircle className="w-8 h-8 mb-2 text-red-400" />
                    <p className="text-xs text-red-200">Generation failed</p>
                    <p className="text-[10px] text-white/50 mt-1 line-clamp-3">{r.error}</p>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--gold)] bg-gradient-to-br from-[var(--ink)] to-black">
                    <Loader2 className="w-8 h-8 animate-spin mb-3" />
                    <p className="text-[10px] uppercase tracking-[0.22em]">{r.status}</p>
                    <p className="text-xs text-white/60 mt-1">2–5 min</p>
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="text-xs text-[var(--muted)] line-clamp-2 mb-2">{r.prompt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--gold)]">{r.duration}s · {r.size}</span>
                  <div className="flex gap-1">
                    {r.status === "ready" && (
                      <a
                        href={`${BACKEND_URL}/api/files/${r.storage_path}`}
                        download
                        className="p-1.5 hover:text-[var(--gold)]"
                        data-testid={`reel-download-${r.reel_id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      onClick={() => remove(r.reel_id)}
                      className="p-1.5 hover:text-red-600"
                      data-testid={`reel-delete-${r.reel_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
